import { getId } from '@ga/utils'

import type { GaApp } from '~/modules/app'
import type { IUser, UserData } from '~/modules/main'

import { mergeUserResponse } from '../utils/merge-user-response'

interface UserSnapshot {
  user: IUser
  gameConfiguration: Awaited<
    ReturnType<GaApp['services']['main']['api']['getGameConfiguration']>
  >
}

import { retryWithDelay } from './main.service'

export class UserService {
  gaApp: GaApp
  isTabActive: boolean
  getUserTimerId: ReturnType<typeof setTimeout> | null
  getUserTimerDisabled: boolean
  private loadUserSnapshotInFlight: Promise<UserSnapshot> | null = null

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
    this.isTabActive = true
    this.getUserTimerId = null
    this.getUserTimerDisabled = false

    /** Слежка за активностью таба, чтобы переключении вкл/выкл таймер запроса за пользователем */
    document.addEventListener('visibilitychange', () => {
      this.checkBrowserTabVisibility()
    })
  }

  /**
   * Получение текущих данных о пользователе из стора
   */
  get data() {
    return this.gaApp.stores.main.user.data
  }

  /**
   * Проверка авторизован ли пользователь
   */
  get isAuthorized() {
    return this.gaApp.stores.main.user.isAuthorized
  }

  /**
   * Метод устанавливает id-сеанса пользователя
   */
  setUserViewGUID() {
    this.gaApp.stores.main.user.viewGUID = getId()
  }

  /**
   * Обновление количества бьютисов (монет)
   * Анимация происходит в хэдере
   */
  updateCoins(value: number) {
    this.gaApp.stores.main.user.coins = value
  }

  /**
   * Проверка баланса монет для прокрутки рулетки
   */
  hasEnoughCoinsForRoulette() {
    const roulettePrice = this.gaApp.stores.main.main.actualRoulettePrice

    const coins = this.gaApp.stores.main.user.coins

    // монет должно быть больше 0 и больше, чем цена прокрутки
    return coins > 0 && coins >= roulettePrice
  }

  /**
   * Получение данных пользователя из ручки юзера
   * проверяем флаг отображение модального окна с ежедневной наградой
   * и обновляем монеты
   */
  async getUser(silent?: boolean) {
    this.clearGetUserTimer()

    try {
      const { user, gameConfiguration } = await this.loadUserSnapshot()
      const previousUser = this.gaApp.stores.main.user.data
      const mergedUser = mergeUserResponse(previousUser, user)

      const {
        showDailyReward,
        showPromoModal,
        showRatingResultsModal,
        dailyRewardCount,
        coins,
        oneSpinPrice,
        freeSpin,
      } = mergedUser

      this.gaApp.stores.main.user.data = mergedUser

      this.gaApp.stores.main.main.roulettePrice = oneSpinPrice
      this.gaApp.stores.main.main.gameConfiguration = gameConfiguration

      if (freeSpin) {
        this.gaApp.stores.main.user.freeSpin = true
      }

      this.gaApp.services.main.main.handleInitialUserModals(
        {
          showPromoModal,
          showRatingResultsModal,
          showDailyReward,
          dailyRewardCount,
        },
        silent,
      )

      this.updateCoins(coins)
    } catch (error) {
      // Проверяем, был ли запрос отменен. Если отменен не логируем, не пробрасываем ошибку
      const typedError = error as { aborted?: boolean }
      if (typedError?.aborted) {
        return
      }

      if (silent) {
        this.gaApp.services.app.rejection.log({
          name: 'main:get-user-data',
          message: 'Ошибка обновления данных юзера по кд',
          errorData: error,
        })
      } else {
        throw error
      }
    } finally {
      if (this.isTabActive && !this.getUserTimerDisabled) {
        // запускаем таймер после каждого запроса
        this.getUserTimerId = setTimeout(() => {
          void this.getUser(true)
        }, 30000)
      }
    }
  }

  /**
   * Один in-flight запрос /user + gameConfiguration.
   * Параллельные getUser (poll, route, auth) делят один snapshot.
   */
  private async loadUserSnapshot(): Promise<UserSnapshot> {
    if (this.loadUserSnapshotInFlight) {
      return this.loadUserSnapshotInFlight
    }

    this.loadUserSnapshotInFlight = (async () => {
      const [user, gameConfiguration] = await Promise.all([
        retryWithDelay(async () => this.gaApp.services.main.api.getUser()),
        retryWithDelay(async () =>
          this.gaApp.services.main.api.getGameConfiguration(),
        ),
      ])

      return { user, gameConfiguration }
    })().finally(() => {
      this.loadUserSnapshotInFlight = null
    })

    return this.loadUserSnapshotInFlight
  }

  /** Очищает таймер запроса данных пользователя */
  clearGetUserTimer() {
    if (this.getUserTimerId) {
      clearTimeout(this.getUserTimerId)
      this.getUserTimerId = null
    }
  }

  /**
   * Останавливает и выключает запросы по кд
   */
  disableGetUserTimer() {
    this.clearGetUserTimer()
    this.getUserTimerDisabled = true
  }

  /**
   * Включает возможность запуска таймера по кд
   */
  enableGetUserTimer() {
    this.getUserTimerDisabled = false
  }

  /**
   * Проверяем активность вкладки
   * И вкл/выкл таймер запроса пользователя, исходя из значения
   */
  checkBrowserTabVisibility() {
    if (document.visibilityState === 'visible') {
      this.isTabActive = true

      if (
        !this.getUserTimerDisabled &&
        this.gaApp.stores.main.user.isAuthorized
      ) {
        void this.getUser(true)
      }
    } else {
      this.isTabActive = false
      this.clearGetUserTimer()
    }
  }

  setAuthorized(value = true) {
    this.gaApp.stores.main.user.isAuthorized = value
  }

  /**
   * Сохранение в стор данных пользователя,
   * полученых из шопа
   */
  setExternalUserData(data: unknown) {
    this.gaApp.stores.main.user.externalData = data
  }

  /**
   * Получение addressParams из шопа
   */
  get addressParams() {
    const externalData = this.gaApp.stores.main.user.externalData as UserData
    return externalData?.addressParams || {}
  }

  async checkUserDataFilled() {
    return this.gaApp.services.app.bridge.sendCheckUserDataFilledMessage()
  }

  async processUserData(data: UserData = {}): Promise<void> {
    try {
      const { ecomToken, phone, ...rest } = data

      this.setExternalUserData(rest as unknown)

      if (ecomToken ?? '') {
        this.gaApp.services.app.auth.setToken(ecomToken)
        this.gaApp.services.app.apm.addLabels({ phone })

        await this.getUser()
        this.setAuthorized()
        this.gaApp.eventBus.publish('user/auth/login')
      } else if (this.gaApp.services.app.mobileOnlyStub.hasAccess === true) {
        this.gaApp.services.app.bridge.sendAuthMessage()
      }
    } catch (error) {
      this.gaApp.services.app.rejection.log({
        name: 'main:proccess-user-data',
        message: 'Ошибка обработки данных юзера, полученных извне',
        errorData: error,
      })
    }
  }
}
